<script setup lang="ts">
import PrimeDialog from 'primevue/dialog'

interface Props {
  /** v-model state. Pass `modelValue` from v-model, or `visible` to mirror the PrimeVue prop. */
  modelValue?: boolean
  /** PrimeVue-compatible alias for `modelValue`. */
  visible?: boolean
  header?: string
  /** Panel width (any CSS length). Default `'400px'`. */
  width?: string
  /** Panel max-height (any CSS length). Default `'90vh'`. */
  maxHeight?: string
  /** Tap on mask dismisses the dialog. Default `true`. */
  dismissable?: boolean
  /** Show the X close icon in the header. Default `true`. */
  showCloseIcon?: boolean
  /** Modal (with mask + focus trap). Default `true`. */
  modal?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  width: '400px',
  maxHeight: '90vh',
  dismissable: true,
  showCloseIcon: true,
  modal: true,
})

defineOptions({ inheritAttrs: false })

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'update:visible', value: boolean): void
  (e: 'hide'): void
}>()

const pt = {
  root: {
    class: 'app-dialog__panel',
    style: { width: props.width, maxWidth: 'calc(100vw - 2rem)', maxHeight: props.maxHeight },
  },
  header: { class: 'app-dialog__header' },
  content: {
    class: 'app-dialog__content',
    style: { overflowY: 'auto', maxHeight: `calc(${props.maxHeight} - 8rem)` },
  },
  mask: { class: 'app-dialog__mask' },
}

// Emit both `update:modelValue` and `update:visible` so callers can use
// either `v-model="show"` or `v-model:visible="show"` — both are natural
// Vue 3 patterns and both must close the dialog when the user dismisses it.
const onHide = () => {
  emit('update:modelValue', false)
  emit('update:visible', false)
  emit('hide')
}
</script>

<template>
  <PrimeDialog
    v-bind="$attrs"
    :visible="modelValue || visible"
    :header="header"
    :dismissable-mask="dismissable"
    :closable="showCloseIcon"
    :modal="modal"
    :pt="pt"
    :block-scroll="true"
    @update:visible="(v: boolean) => !v && onHide()"
  >
    <template v-if="$slots.header" #header>
      <slot name="header" />
    </template>
    <slot />
    <template v-if="$slots.footer" #footer>
      <slot name="footer" />
    </template>
  </PrimeDialog>
</template>
